/* globals Rollbar */

import { CouponService } from "./coupon_service";
import { formatterCurrency, getLowerCaseInputValue } from "./formatters";
import {
  blurOnEnterKeyupHandler,
  disableInputField,
  disableSubmitButtonElement,
  forceBlurOnEventHandler,
  objectToInlineCss,
  setElementHiddenClass
} from "./helper_functions";

const createCouponErrorSpan = ({ errorMessage = "Cupom inválido", className = "alert-message" } = {}) => {
  const couponErrorMessageSpan = document.createElement("span");
  couponErrorMessageSpan.className = className;
  couponErrorMessageSpan.style = objectToInlineCss({
    color: "#B91C1C",
    "font-weight": 500,
    "font-size": "14px",
    "font-family": "Inter, sans-serif",
    "padding-top": "6px",
  });

  couponErrorMessageSpan.textContent = errorMessage;
  return couponErrorMessageSpan;
};

/**
 *
 * @param {{
 *   elements: {
 *     clearCouponButton: HTMLSpanElement;
 *     couponInput: HTMLInputElement;
 *     discountAmountSpan: HTMLSpanElement;
 *     errorSpanContainer: HTMLElement;
 *     formAmountHiddenInput: HTMLInputElement;
 *     paymentTotalSpan: HTMLSpanElement;
 *     submitCouponButton: HTMLSpanElement;
 *     emailInput: HTMLInputElement;
 *     submitFormButton: HTMLInputElement;
 *   };
 *   errorMessageOptions: {
 *     errorMessage: string;
 *     className: string;
 *   };
 * }} params
 * @returns
 */
export const couponFormFactory = (params = {}) => {
  const { elements, productId, errorMessageOptions = {} } = params;
  if (!elements || Object.keys(elements).length < 1) {
    throw new Error("Should provide elements references!");
  }
  if (!productId || !productId?.length) {
    throw new Error("Should provide productId value!");
  }

  if (new URLSearchParams(window.location.search).has("cupom")) {
    const { clearCouponButton } = elements;

    function removeCouponFromUrlHandler () {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.delete("cupom");
      window.location.assign(currentUrl);
    }

    if (clearCouponButton) {
      clearCouponButton.addEventListener("click", removeCouponFromUrlHandler);
    } else if (Rollbar) {
      Rollbar.error("Missing clear coupon button reference", {
        url: window.location.href
      });
    }

    return { fixedCoupon: true };
  }

  const {
    clearCouponButton,
    couponInput,
    discountAmountSpan,
    errorSpanContainer,
    formAmountHiddenInput,
    paymentTotalSpan,
    submitCouponButton,
    emailInput,
    submitFormButton
  } = elements;

  /** @type {Function[]} */
  let couponChangeEventListeners = [];

  let currentlyActiveCoupon = "";

  let validEmail = null;

  const couponErrorSpan = createCouponErrorSpan(errorMessageOptions);

  function addCouponChangeListener (listener) {
    if (!couponChangeEventListeners.some((registeredListener) => registeredListener === listener)) {
      couponChangeEventListeners.push(listener);
    }
  }

  function removeCouponChangeListener (listener) {
    couponChangeEventListeners = couponChangeEventListeners
      .filter((registeredListener) => registeredListener !== listener);
  }

  /**
   * Publish data to listeners
   *
   * @param {{
   *   amountOff: string
   *   couponId: string
   *   discountAmount: string
   *   discountedAmount: string
   *   percentOff: string
   * }} couponData
   * @param {boolean=} clear
   */
  function pushCouponChangeToListeners (couponData, clear) {
    couponChangeEventListeners.forEach((listener) => {
      try {
        listener?.(couponData, clear);
      } catch (error) {
        console.error("Failed to call listener:", { listener });
      }
    });
  }

  function couponInputClickHandler () {
    setElementHiddenClass(submitCouponButton, false);
    disableInputField(submitCouponButton, false, !currentlyActiveCoupon.length);
    removeErrorSpan();
  }

  function removeErrorSpan () {
    if (couponErrorSpan.parentElement) {
      couponErrorSpan.parentElement.removeChild(couponErrorSpan);
    }
  }

  /**
   * Appends errorSpan into the given parentElement
   * @param {HTMLElement} parentElement to append the errorSpan as child
   */
  function appendErrorSpan (parentElement = errorSpanContainer) {
    removeErrorSpan();
    parentElement.appendChild(couponErrorSpan);
  }

  /**
   * Fetch coupon data from server relative to the current product
   * @param {string} coupon
   * @param {string} amount
   * @param {string} email
   */
  const fetchCoupon = async (coupon, amount, email) => {
    const couponData = await CouponService.getCoupon(coupon, amount, productId, email)
      .catch((error) => {
        console.error(error);
        appendErrorSpan(errorSpanContainer);
      });
    if (!couponData) {
      return null;
    }
    const formattedDiscountedAmount = formatterCurrency.format(couponData.discountedAmount / 100);
    const formattedDiscount = formatterCurrency.format(couponData.discountAmount / 100);
    const formattedPixDiscountedAmount = formatterCurrency.format(couponData.pixDiscountedAmount / 100);
    const formattedPixAmount = formatterCurrency.format(couponData.pixAmount / 100);

    return {
      ...couponData,
      formattedDiscount,
      formattedDiscountedAmount,
      formattedPixAmount,
      formattedPixDiscountedAmount
    };
  };

  /**
   * Handles loss of focus in couponInput
   * @param {FocusEvent} event
   */
  function couponInputBlurHandler (event) {
    event.preventDefault();
    removeErrorSpan();

    const couponName = couponInput.value.toUpperCase();

    setElementHiddenClass(submitCouponButton, true);
    disableInputField(couponInput, true, false);

    if (couponName.trim().length < 1 && currentlyActiveCoupon) {
      couponInput.value = currentlyActiveCoupon;
      disableInputField(couponInput, false, false);
      return;
      // eslint-disable-next-line eqeqeq
    } else if (couponName.trim() == currentlyActiveCoupon && validEmail == getLowerCaseInputValue(emailInput)) {
      disableInputField(couponInput, false, false);
      return;
    } else if (couponName.trim().length < 1) {
      disableInputField(couponInput, false, true);
      return;
    }

    // disable submit form while the coupon is validating
    disableSubmitButtonElement(submitFormButton, true);

    const lowercasedEmailValue = getLowerCaseInputValue(emailInput);

    fetchCoupon(couponName, formAmountHiddenInput.value, lowercasedEmailValue)
      .then((couponData) => {
        if (couponData !== null) {
          // save the previous email used to validate coupon
          validEmail = lowercasedEmailValue;
          currentlyActiveCoupon = couponName;
          couponInput.value = couponName;
          discountAmountSpan.textContent = couponData.formattedDiscount;
          paymentTotalSpan.textContent = couponData.formattedDiscountedAmount;
          // disable input to not edit coupon after a validation
          disableInputField(couponInput, true, false);
          setElementHiddenClass(discountAmountSpan, false);
          setElementHiddenClass(clearCouponButton, false);
        } else if (validEmail !== lowercasedEmailValue) {
          // if email input value is different from the first email filled, coupon is invalid
          removeCouponHandler();
        } else {
          couponInput.value = currentlyActiveCoupon;
        }
        disableInputField(submitCouponButton, true, false);

        pushCouponChangeToListeners(couponData);
      })
      .finally(() => {
        // deal with coupon input disable
        // when currentlyActiveCoupon has not length, disable is false
        // when currentlyActiveCoupon has length, disable is true (coupon is already validated)
        disableInputField(couponInput, currentlyActiveCoupon.length, !currentlyActiveCoupon.length);
        disableSubmitButtonElement(submitFormButton, false);
      });
  }

  function removeCouponHandler () {
    removeErrorSpan();
    setElementHiddenClass(submitCouponButton, false);
    disableInputField(couponInput, false, true);
    pushCouponChangeToListeners(null, true);
    setElementHiddenClass(discountAmountSpan, true);
    setElementHiddenClass(clearCouponButton, true);
    setElementHiddenClass(submitCouponButton, true);
    currentlyActiveCoupon = "";
  }

  // check coupon after email is filled
  emailInput.addEventListener("blur", couponInputBlurHandler);

  couponInput.addEventListener("blur", couponInputBlurHandler);

  couponInput.addEventListener("click", couponInputClickHandler);

  couponInput.addEventListener("keyup", blurOnEnterKeyupHandler);

  submitCouponButton.addEventListener("click", forceBlurOnEventHandler);

  clearCouponButton.addEventListener("click", removeCouponHandler);

  return {
    fixedCoupon: false,
    couponInput,
    clearCouponButton,
    submitCouponButton,
    discountAmountSpan,
    paymentTotalSpan,
    addCouponChangeListener,
    removeCouponChangeListener,
    appendErrorSpan,
    removeErrorSpan,
    elements
  };
};
